/**
 * Single source of truth for all theming default values.
 *
 * This file replaces scattered defaults previously found in:
 * - src/features/theme/css-variables.ts (inline fallback values)
 * - src/features/theme/gamification-theme.ts (DEFAULTS object)
 * - src/lib/tenant/TenantSettingsProvider.tsx (fallback values)
 * - Various field definition files with defaultValue properties
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Named shadow intensity levels. */
export type ShadowStyle = "none" | "subtle" | "medium" | "strong";

/** Named spacing density levels. */
export type DensityOption = "extra-compact" | "compact" | "normal" | "relaxed" | "extra-relaxed";

/** Union of all global component slot names. */
export type GlobalSlotName = keyof typeof SLOT_DEFAULTS;

// ---------------------------------------------------------------------------
// Named colors seed list
// ---------------------------------------------------------------------------

/**
 * Seed color palette used as defaults when a tenant has not configured custom colors.
 * Each entry maps a semantic color name to a hex value.
 */
export const DEFAULT_NAMED_COLORS = [
    // Brand colors
    { name: "primary", value: "#3b82f6" },
    { name: "primaryForeground", value: "#ffffff" },
    { name: "secondary", value: "#6b7280" },
    { name: "secondaryForeground", value: "#ffffff" },
    { name: "accent", value: "#8b5cf6" },
    { name: "accentForeground", value: "#ffffff" },
    // Surfaces
    { name: "background", value: "#ffffff" },
    { name: "foreground", value: "#0f172a" },
    { name: "card", value: "#ffffff" },
    { name: "cardForeground", value: "#0f172a" },
    { name: "muted", value: "#f1f5f9" },
    { name: "mutedForeground", value: "#64748b" },
    // Borders & input
    { name: "border", value: "#e2e8f0" },
    { name: "input", value: "#e2e8f0" },
    { name: "ring", value: "#3b82f6" },
    // Semantic
    { name: "success", value: "#22c55e" },
    { name: "successForeground", value: "#ffffff" },
    { name: "warning", value: "#f59e0b" },
    { name: "warningForeground", value: "#ffffff" },
    { name: "error", value: "#ef4444" },
    { name: "errorForeground", value: "#ffffff" },
    { name: "info", value: "#3b82f6" },
    { name: "infoForeground", value: "#ffffff" },
] as const;

// ---------------------------------------------------------------------------
// Global typography / layout defaults
// ---------------------------------------------------------------------------

/**
 * Global design token defaults for typography, border radius, shadow style, and spacing density.
 * Used when a tenant has not overridden any of these values.
 */
export const DEFAULT_GLOBALS = {
    fontFamily: "Inter",
    fontFamilyHeading: "inherit",
    baseFontSize: "16px",
    radius: "0.5rem",
    shadowStyle: "subtle" as ShadowStyle,
    density: "normal" as DensityOption,
} as const;

// ---------------------------------------------------------------------------
// Shadow presets
// ---------------------------------------------------------------------------

/**
 * Box-shadow values for each named shadow intensity level.
 * Values are copied verbatim from src/features/theme/css-variables.ts.
 */
export const SHADOW_PRESETS = {
    none: {
        sm: "none",
        md: "none",
        lg: "none",
    },
    subtle: {
        sm: "0 1px 2px 0 rgb(0 0 0 / 0.05)",
        md: "0 4px 6px -1px rgb(0 0 0 / 0.07), 0 2px 4px -2px rgb(0 0 0 / 0.07)",
        lg: "0 10px 15px -3px rgb(0 0 0 / 0.07), 0 4px 6px -4px rgb(0 0 0 / 0.07)",
    },
    medium: {
        sm: "0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)",
        md: "0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)",
        lg: "0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)",
    },
    strong: {
        sm: "0 1px 3px 0 rgb(0 0 0 / 0.15), 0 1px 2px -1px rgb(0 0 0 / 0.15)",
        md: "0 4px 6px -1px rgb(0 0 0 / 0.15), 0 2px 4px -2px rgb(0 0 0 / 0.15)",
        lg: "0 20px 25px -5px rgb(0 0 0 / 0.15), 0 8px 10px -6px rgb(0 0 0 / 0.15)",
    },
} as const satisfies Record<ShadowStyle, { sm: string; md: string; lg: string }>;

// ---------------------------------------------------------------------------
// Density presets
// ---------------------------------------------------------------------------

/**
 * Spacing multiplier strings for each density level.
 * Applied as a CSS custom property that scales component padding/gap values.
 */
export const DENSITY_PRESETS = {
    "extra-compact": "0.75",
    compact: "0.875",
    normal: "1",
    relaxed: "1.25",
    "extra-relaxed": "1.5",
} as const satisfies Record<DensityOption, string>;

// ---------------------------------------------------------------------------
// Component slot defaults
// ---------------------------------------------------------------------------

/**
 * Sensible default styles for each global component slot.
 * These are applied when the tenant has not configured overrides for a slot.
 */
export const SLOT_DEFAULTS = {
    header: {
        backgroundColor: "#1f2937",
        textColor: "#ffffff",
    },
    secondaryHeader: {
        backgroundColor: "#374151",
        textColor: "#ffffff",
    },
    brandRow: {
        backgroundColor: "#111827",
        textColor: "#ffffff",
    },
    footer: {
        backgroundColor: "#1f2937",
        textColor: "#ffffff",
    },
    pageBackground: {
        backgroundColor: "#f8f9fa",
        textColor: "#0f172a",
    },
    card: {
        backgroundColor: "#ffffff",
        textColor: "#0f172a",
        shadow: "subtle" as ShadowStyle,
    },
    tabBar: {
        backgroundColor: "card",
        textColor: "foreground",
        shadow: "subtle" as ShadowStyle,
    },
    matchCard: {
        backgroundColor: "#ffffff",
        textColor: "#0f172a",
    },
    playerCard: {
        backgroundColor: "#ffffff",
        textColor: "#0f172a",
        shadow: "subtle" as ShadowStyle,
    },
    oddsButton: {
        backgroundColor: "#f1f5f9",
        textColor: "#0f172a",
    },
    gamificationCard: {
        gradientFrom: "primary",
        gradientTo: "accent",
        gradientDirection: "to-right",
        textColor: "#ffffff",
    },
    challengeZone: {
        gradientFrom: "accent",
        gradientTo: "primary",
        gradientDirection: "to-right",
        textColor: "#ffffff",
    },

    // --- Element-kind slots ---
    primaryButton: {
        backgroundColor: "primary",
        textColor: "primaryForeground",
    },
    plainButton: {
        backgroundColor: "transparent",
        textColor: "foreground",
    },
    link: {
        textColor: "foreground",
    },
    // --- Breadcrumbs slot (NEW — navigation crumbs on inner pages) ---
    breadcrumbs: {
        textColor: "mutedForeground",
    },
    tab: {
        backgroundColor: "primary",
        textColor: "primaryForeground",
    },

    // --- Form input slot (NEW — wired by foundation, consumers migrated later) ---
    input: {
        backgroundColor: "card",
        textColor: "foreground",
        borderColor: "border",
        borderWidth: "1px",
        borderStyle: "solid",
    },

    // --- Modal dialog body slot (NEW) ---
    modal: {
        backgroundColor: "card",
        textColor: "cardForeground",
        shadow: "strong" as ShadowStyle,
    },

    // --- Tabular data slot (NEW — standings, stats, leaderboards) ---
    table: {
        backgroundColor: "card",
        textColor: "foreground",
        borderColor: "border",
    },

    // --- Header-context button slot (NEW) ---
    headerButton: {
        backgroundColor: "transparent",
        textColor: "#ffffff",
    },

    // --- Neutral-context link slot (NEW — for dense list/table/grid contexts) ---
    linkNeutral: {
        textColor: "foreground",
    },
} as const;

// ---------------------------------------------------------------------------
// Slot name list
// ---------------------------------------------------------------------------

/**
 * Array of all global component slot names, derived from SLOT_DEFAULTS keys.
 * Useful for iteration (e.g. building CMS select options or rendering slot editors).
 */
export const GLOBAL_SLOT_NAMES = Object.keys(SLOT_DEFAULTS) as GlobalSlotName[];
